import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

export interface MetaPixelParams {
  [key: string]: unknown;
}

export interface MetaUserData {
  email?: string;
  phone?: string;
}

export interface MetaBrowserContext {
  eventSourceUrl?: string;
  fbp?: string;
  fbc?: string;
}

export interface MetaTrackOptions {
  eventId?: string;
}

interface MetaEventOptions {
  eventID: string;
  em?: string;
  ph?: string;
}

@Injectable({ providedIn: 'root' })
export class MetaPixelService {
  private readonly platformId = inject(PLATFORM_ID);
  private lastTrackedPath: string | null = null;

  pageView(pagePath?: string): void {
    if (!this.isReady()) return;

    const normalizedPath = pagePath ?? this.currentPath();
    if (normalizedPath === this.lastTrackedPath) return;

    this.lastTrackedPath = normalizedPath;
    this.track('PageView');
  }

  viewContent(params: MetaPixelParams): void {
    this.track('ViewContent', params);
  }

  lead(params: MetaPixelParams): void {
    this.track('Lead', params);
  }

  completeRegistration(params: MetaPixelParams): void {
    this.track('CompleteRegistration', params);
  }

  custom(eventName: string, params: MetaPixelParams = {}): void {
    if (!this.isReady()) return;
    this.emitCustom(eventName, params);
  }

  trackLead(data?: { content_name?: string }): void {
    this.track('Lead', data ?? {});
  }

  trackViewContent(contentName: string): void {
    this.track('ViewContent', { content_name: contentName });
  }

  trackContact(): void {
    this.track('Contact');
  }

  trackSectionView(section: string): void {
    this.custom('SectionView', { section });
  }

  createEventId(): string {
    return this.generateEventId();
  }

  getBrowserContext(): MetaBrowserContext {
    if (!isPlatformBrowser(this.platformId)) return {};

    return {
      eventSourceUrl: this.currentUrl(),
      fbp: this.readCookie('_fbp') || undefined,
      fbc: this.readFbc() || undefined,
    };
  }

  async trackWithUserData(
    eventName: string,
    params: MetaPixelParams,
    userData: MetaUserData,
    trackOptions: MetaTrackOptions = {},
  ): Promise<string> {
    if (!this.isReady()) return '';
    const eventId = trackOptions.eventId ?? this.createEventId();
    const options = await this.buildEventOptions(userData, eventId);
    this.emitTrack(eventName, params, options);
    return eventId;
  }

  async hashString(value: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId) || !crypto?.subtle) return '';
    const encoded = new TextEncoder().encode(value);
    const buffer = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private track(eventName: string, params: MetaPixelParams = {}): void {
    if (!this.isReady()) return;
    this.emitTrack(eventName, params);
  }

  private emitTrack(
    eventName: string,
    params: MetaPixelParams = {},
    options: MetaEventOptions = this.buildBaseEventOptions(),
  ): void {
    window.fbq?.('track', eventName, params, options);
  }

  private emitCustom(
    eventName: string,
    params: MetaPixelParams = {},
    options: MetaEventOptions = this.buildBaseEventOptions(),
  ): void {
    window.fbq?.('trackCustom', eventName, params, options);
  }

  private async buildEventOptions(
    userData: MetaUserData = {},
    eventId = this.createEventId(),
  ): Promise<MetaEventOptions> {
    const options = this.buildBaseEventOptions(eventId);
    const email = userData.email?.toLowerCase().trim();
    const phone = userData.phone?.replace(/\D/g, '');

    if (email) {
      options.em = await this.hashString(email);
    }

    if (phone) {
      options.ph = await this.hashString(phone);
    }

    return options;
  }

  private buildBaseEventOptions(eventId = this.createEventId()): MetaEventOptions {
    return { eventID: eventId };
  }

  private generateEventId(): string {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }

  private isReady(): boolean {
    return isPlatformBrowser(this.platformId) && typeof window.fbq === 'function';
  }

  private currentPath(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    return window.location.pathname + window.location.search;
  }

  private currentUrl(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    return window.location.href;
  }

  private readCookie(name: string): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private readFbc(): string {
    const existing = this.readCookie('_fbc');
    if (existing) return existing;
    if (!isPlatformBrowser(this.platformId)) return '';

    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (!fbclid) return '';

    return `fb.1.${Date.now()}.${fbclid}`;
  }
}
